import {useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import IconArrowUpRight from "../../assets/arrow-up-right.svg?react";
import {dibAppApi} from "../../dibAppApi/dibAppApi";
import {deslugify} from "./contentUtils";
import {useContentParams} from "./router";
import {useContentTitle} from "./useContentTitle";
import layoutClasses from "./ContentLayout.module.css";
import classes from "./ExternalFallback.module.css";

function getDibBaseUrl(): string {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "ai.dibdev.no") {
        return "https://dib.dibdev.no";
    }
    return "https://app.dib.no";
}

/**
 * Fallback for resource types that ContentPage doesn't support yet.
 * ContentPage only renders "vanlig" HTML content (resourceTypeId "19").
 * For everything else — PDFs, checklists, Office documents, etc. — we
 * link the user out to the legacy dib app where full rendering exists.
 */
export const ExternalFallback = ({
    resourceId,
    segmentId,
}: {
    resourceId: string;
    segmentId: string | undefined;
}) => {
    const location = useLocation();
    const {t} = useTranslation();
    const {topicName} = useContentParams();
    const {data: documentData} = dibAppApi.endpoints.getDocument.useQuery({
        resourceId,
        segmentId,
    });

    // The document query gives no name for the resource types that this
    // fallback covers. The slug in the URL is then the only name available.
    const title = documentData?.name || deslugify(topicName);

    useContentTitle(title || undefined);

    return (
        <div className={layoutClasses.page}>
            <article className={layoutClasses.article}>
                <div className={layoutClasses.articleInner}>
                    <div className={classes.fallback}>
                        <span className={classes.fallbackLabel}>
                            {t("Phrase.AvailableOnDib", {
                                defaultValue: "Available on dib",
                            })}
                        </span>
                        {title && (
                            <h2 className={classes.fallbackTitle}>{title}</h2>
                        )}
                        <a
                            className={classes.fallbackCta}
                            href={`${getDibBaseUrl()}${location.pathname}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t("Action.OpenInDib", {defaultValue: "Open in dib"})}
                            <IconArrowUpRight
                                className={classes.fallbackCtaIcon}
                            />
                        </a>
                    </div>
                </div>
            </article>
        </div>
    );
};
